import React, { useState, useRef, useMemo } from 'react';

type DataPoint = { label: string; value: number; prior: number };

// Mock 6-month weekly series showing a dip-and-recovery narrative:
// starts around $2080, troughs in July around $1680, recovers to ~$2600 by October.
// Replace with real data from the backend when the metrics endpoint is wired up.
const DATA_6M: DataPoint[] = [
  { label: 'May 1',  value: 2080, prior: 1920 },
  { label: 'May 8',  value: 2050, prior: 1940 },
  { label: 'May 15', value: 2020, prior: 1960 },
  { label: 'May 22', value: 1980, prior: 1970 },
  { label: 'May 29', value: 1940, prior: 1980 },
  { label: 'Jun 5',  value: 1900, prior: 1990 },
  { label: 'Jun 12', value: 1860, prior: 2000 },
  { label: 'Jun 19', value: 1820, prior: 2010 },
  { label: 'Jun 26', value: 1780, prior: 2020 },
  { label: 'Jul 3',  value: 1730, prior: 2030 },
  { label: 'Jul 10', value: 1680, prior: 2040 },
  { label: 'Jul 17', value: 1720, prior: 2050 },
  { label: 'Jul 24', value: 1790, prior: 2060 },
  { label: 'Jul 31', value: 1870, prior: 2070 },
  { label: 'Aug 7',  value: 1960, prior: 2080 },
  { label: 'Aug 14', value: 2060, prior: 2090 },
  { label: 'Aug 21', value: 2170, prior: 2100 },
  { label: 'Aug 28', value: 2260, prior: 2110 },
  { label: 'Sep 4',  value: 2340, prior: 2120 },
  { label: 'Sep 11', value: 2410, prior: 2130 },
  { label: 'Sep 18', value: 2470, prior: 2140 },
  { label: 'Sep 25', value: 2520, prior: 2150 },
  { label: 'Oct 2',  value: 2560, prior: 2160 },
  { label: 'Oct 10', value: 2600, prior: 2170 },
];

const PERIODS = ['1M', '3M', '6M', 'YTD', '1Y', 'All'] as const;
type Period = typeof PERIODS[number];

const MONTHS = ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];

// === Styles ===
const CARD: React.CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E7EAF0',
  borderRadius: 10,
  padding: '20px 22px',
  marginBottom: 14,
};
const HEAD: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 14,
  gap: 14,
  flexWrap: 'wrap',
};
const LEFT: React.CSSProperties = { flex: 1, minWidth: 220 };
const TITLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  color: '#66748B',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  marginBottom: 6,
};
const STAT_ROW: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' };
const STAT: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 800,
  color: '#0E1A1A',
  lineHeight: 1,
};
const DELTA_PILL_POS: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  background: '#E8F5F2',
  color: '#0F6E56',
  fontSize: 12,
  fontWeight: 700,
  padding: '4px 10px',
  borderRadius: 999,
};
const DELTA_PILL_NEG: React.CSSProperties = {
  ...DELTA_PILL_POS,
  background: 'rgba(239,68,68,0.1)',
  color: '#B91C1C',
};
const SUB: React.CSSProperties = { fontSize: 12, color: '#66748B', marginTop: 6 };

const RIGHT: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 };
const PERIOD_GROUP: React.CSSProperties = {
  display: 'inline-flex',
  background: '#F3F6F9',
  borderRadius: 999,
  padding: 3,
  fontSize: 11,
  fontWeight: 600,
};
const PERIOD_PILL: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 999,
  color: '#66748B',
  cursor: 'pointer',
  transition: 'all 0.12s',
};
const PERIOD_PILL_ON: React.CSSProperties = {
  background: '#FFFFFF',
  color: '#0E1A1A',
  boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
};
const COMPARE_ROW: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 11,
  color: '#66748B',
  cursor: 'pointer',
  userSelect: 'none',
};
const TOGGLE_OFF: React.CSSProperties = {
  width: 28,
  height: 16,
  background: '#CBD5E1',
  borderRadius: 999,
  position: 'relative',
  transition: 'background 0.15s',
  flexShrink: 0,
};
const TOGGLE_ON: React.CSSProperties = { ...TOGGLE_OFF, background: '#15A08C' };
const TOGGLE_DOT_OFF: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  left: 2,
  width: 12,
  height: 12,
  background: '#FFFFFF',
  borderRadius: '50%',
  transition: 'left 0.15s',
};
const TOGGLE_DOT_ON: React.CSSProperties = { ...TOGGLE_DOT_OFF, left: 14 };

const CHART_WRAP: React.CSSProperties = { position: 'relative', height: 220, margin: '0 -4px' };
const Y_LABELS: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: -2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '6px 0',
  fontSize: 10,
  color: '#66748B',
  pointerEvents: 'none',
};
const X_AXIS: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 10,
  color: '#66748B',
  marginTop: 8,
  padding: '0 10px',
};

const TOOLTIP: React.CSSProperties = {
  position: 'absolute',
  top: 10,
  background: '#0E1A1A',
  color: '#FFFFFF',
  padding: '8px 10px',
  borderRadius: 7,
  fontSize: 11,
  lineHeight: 1.3,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  minWidth: 130,
  transform: 'translateX(-50%)',
  pointerEvents: 'none',
  zIndex: 10,
};
const TOOLTIP_DATE: React.CSSProperties = { color: '#94A3B8', fontSize: 10, marginBottom: 2 };
const TOOLTIP_VALUE: React.CSSProperties = { fontSize: 13, fontWeight: 700 };
const TOOLTIP_COMPARE: React.CSSProperties = {
  fontSize: 10,
  color: '#94A3B8',
  marginTop: 3,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
};

export default function IncomeChart(): React.ReactElement {
  const [period, setPeriod] = useState<Period>('6M');
  const [compare, setCompare] = useState(true);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const chartRef = useRef<SVGSVGElement>(null);

  // For now we only have one series; period selector swaps to the same data.
  // When the backend wires up, pass different arrays per period.
  const data = DATA_6M;

  // Chart geometry
  const chartWidth = 600;
  const chartHeight = 200;
  const maxY = useMemo(() => {
    const m = Math.max(...data.map((d) => Math.max(d.value, d.prior)));
    return Math.ceil((m * 1.1) / 500) * 500; // round up to nearest $500
  }, [data]);

  // Find the trough (lowest value) index to split the line into red (decline) and teal (recovery)
  const troughIdx = useMemo(() => {
    let minIdx = 0;
    for (let i = 1; i < data.length; i++) {
      if (data[i].value < data[minIdx].value) minIdx = i;
    }
    return minIdx;
  }, [data]);

  const points = useMemo(
    () =>
      data.map((d, i) => ({
        x: (i / (data.length - 1)) * chartWidth,
        y: chartHeight - (d.value / maxY) * chartHeight,
        priorY: chartHeight - (d.prior / maxY) * chartHeight,
        data: d,
      })),
    [data, maxY]
  );

  // Build paths
  const toLineD = (pts: typeof points): string =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const toPriorD = (pts: typeof points): string =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.priorY.toFixed(1)}`).join(' ');

  const redPoints = points.slice(0, troughIdx + 1);
  const tealPoints = points.slice(troughIdx);
  const redLineD = toLineD(redPoints);
  const tealLineD = toLineD(tealPoints);
  const priorD = toPriorD(points);

  const redAreaD = `${redLineD} L ${redPoints[redPoints.length - 1].x.toFixed(1)} ${chartHeight} L 0 ${chartHeight} Z`;
  const tealAreaD = `${tealLineD} L ${chartWidth} ${chartHeight} L ${tealPoints[0].x.toFixed(1)} ${chartHeight} Z`;

  // Totals + delta
  const periodTotal = data.reduce((s, d) => s + d.value, 0);
  const priorTotal = data.reduce((s, d) => s + d.prior, 0);
  const delta = periodTotal - priorTotal;
  const deltaPct = priorTotal > 0 ? (delta / priorTotal) * 100 : 0;
  const positive = delta >= 0;

  // Hover
  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!chartRef.current) return;
    const rect = chartRef.current.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const frac = Math.max(0, Math.min(1, relX / rect.width));
    const idx = Math.round(frac * (data.length - 1));
    setHoverIdx(idx);
  };

  // Y-axis labels (4 ticks)
  const yTicks = useMemo(() => {
    const step = maxY / 3;
    return [maxY, maxY - step, maxY - 2 * step, 0].map((v) =>
      v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : `$${Math.round(v)}`
    );
  }, [maxY]);

  const hoverPoint = hoverIdx !== null ? points[hoverIdx] : null;
  const hoverIsRed = hoverIdx !== null && hoverIdx <= troughIdx;

  return (
    <div style={CARD}>
      <div style={HEAD}>
        <div style={LEFT}>
          <div style={TITLE}>Income over time</div>
          <div style={STAT_ROW}>
            <div style={STAT}>${periodTotal.toLocaleString()}</div>
            <div style={positive ? DELTA_PILL_POS : DELTA_PILL_NEG}>
              {positive ? '↑' : '↓'} {Math.abs(deltaPct).toFixed(1)}%
            </div>
          </div>
          <div style={SUB}>Past 6 months vs. previous 6 months</div>
        </div>

        <div style={RIGHT}>
          <div style={PERIOD_GROUP}>
            {PERIODS.map((p) => (
              <span
                key={p}
                style={period === p ? { ...PERIOD_PILL, ...PERIOD_PILL_ON } : PERIOD_PILL}
                onClick={() => setPeriod(p)}
              >
                {p}
              </span>
            ))}
          </div>
          <div style={COMPARE_ROW} onClick={() => setCompare(!compare)}>
            <div style={compare ? TOGGLE_ON : TOGGLE_OFF}>
              <div style={compare ? TOGGLE_DOT_ON : TOGGLE_DOT_OFF} />
            </div>
            <span>Compare to previous period</span>
          </div>
        </div>
      </div>

      <div style={CHART_WRAP}>
        <svg
          ref={chartRef}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%', display: 'block' }}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="novalaRedGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#EF4444" stopOpacity={0.22} />
              <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="novalaTealGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#15A08C" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#15A08C" stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Gridlines */}
          {[0, 0.333, 0.667, 1].map((frac) => (
            <line
              key={frac}
              x1={0}
              y1={chartHeight * frac}
              x2={chartWidth}
              y2={chartHeight * frac}
              stroke="#F3F6F9"
              strokeWidth={1}
            />
          ))}

          {/* Prior period dashed red (compare on) */}
          {compare && (
            <path
              d={priorD}
              stroke="#EF4444"
              strokeWidth={1.8}
              strokeDasharray="4 4"
              fill="none"
              opacity={0.45}
            />
          )}

          {/* Red segment (decline) */}
          <path d={redAreaD} fill="url(#novalaRedGrad)" />
          <path
            d={redLineD}
            stroke="#EF4444"
            strokeWidth={2.6}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Teal segment (recovery) */}
          <path d={tealAreaD} fill="url(#novalaTealGrad)" />
          <path
            d={tealLineD}
            stroke="#15A08C"
            strokeWidth={2.6}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Hover crosshair + dot */}
          {hoverPoint && (
            <>
              <line
                x1={hoverPoint.x}
                y1={0}
                x2={hoverPoint.x}
                y2={chartHeight}
                stroke="rgba(14,26,26,0.2)"
                strokeWidth={1}
              />
              <circle
                cx={hoverPoint.x}
                cy={hoverPoint.y}
                r={5}
                fill={hoverIsRed ? '#EF4444' : '#15A08C'}
                stroke="#FFFFFF"
                strokeWidth={2}
              />
            </>
          )}
        </svg>

        {/* Y-axis labels */}
        <div style={Y_LABELS}>
          {yTicks.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>

        {/* Hover tooltip */}
        {hoverPoint && (
          <div
            style={{
              ...TOOLTIP,
              left: `${(hoverPoint.x / chartWidth) * 100}%`,
            }}
          >
            <div style={TOOLTIP_DATE}>{hoverPoint.data.label}, 2026</div>
            <div style={TOOLTIP_VALUE}>${hoverPoint.data.value.toLocaleString()}</div>
            {compare && (
              <div style={TOOLTIP_COMPARE}>
                <span>vs prior</span>
                <span
                  style={{
                    color: hoverPoint.data.value >= hoverPoint.data.prior ? '#10B981' : '#EF4444',
                    fontWeight: 600,
                  }}
                >
                  {hoverPoint.data.value >= hoverPoint.data.prior ? '↑' : '↓'} $
                  {Math.abs(hoverPoint.data.value - hoverPoint.data.prior).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={X_AXIS}>
        {MONTHS.map((m) => (
          <span key={m}>{m}</span>
        ))}
      </div>
    </div>
  );
}
